<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
    post: {
        type: Object,
        required: true
    },
    layout: {
        type: String,
        default: null
    },
    image: {
        type: String,
        default: null
    }
});

const store = useStore();

const formattedDate = computed(() => {
    const publishedAt = new Date(props.post.published_at);
    const now = new Date();
    const diffMs = now - publishedAt;
    const diffMinutesTotal = diffMs / (1000 * 60);
    const diffHours = diffMinutesTotal / 60;
    const region = window?.meta?.region_configuration?.region || 'en';

    if (diffHours < 12) {
        if (diffHours < 1) {
            const diffMinutes = Math.round(diffMinutesTotal);

            if (diffMinutes < 1) {
                return region === 'de' ? 'gerade eben' : 'just now';
            }

            if (region === 'de') {
                return `vor ${diffMinutes} Minute${diffMinutes === 1 ? '' : 'n'}`;
            }

            return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        }

        const hours = Math.floor(diffHours);

        if (region === 'de') {
            return `vor ${hours} Stunde${hours === 1 ? '' : 'n'}`;
        }

        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const day = String(publishedAt.getDate()).padStart(2, '0');
    const month = String(publishedAt.getMonth() + 1).padStart(2, '0');
    const year = publishedAt.getFullYear();

    return region === 'de' ? `${day}.${month}.${year}` : `${year}-${month}-${day}`;
});

const postImage = computed(() => {
    return props.post.image_lg || props.image || props.post.image || '';
});

const postLink = computed(() => {
    const appType = store.getters['settings/getApplicationType'];

    return appType === 'backend' ? '/dashboard/hkcmnews/' + props.post.slug.name : '/hkcmnews/' + props.post.slug.name;
});
</script>

<template>
    <router-link class="cursor-pointer group relative border-b border-pimary p-2 lg:p-3 rounded-md bg-white block" :to="postLink">
        <div class="w-full">
            <div class="flex flex-row gap-4 items-center justify-between">
                <small class="block">
                    {{ formattedDate }}
                </small>

                <div v-if="post.postCategories && post.postCategories.data.length" class="inline-flex flex-row">
                    <div v-for="category in post.postCategories.data.slice(0, 5)" :key="category.id" class="border border-blue text-blue text-xs px-1.5 rounded-full">
                        {{ $translate(category.name) }}
                    </div>
                </div>
            </div>

            <div class="flex flex-row gap-4 mt-2">
                <div class="w-full flex flex-col justify-between gap-2">
                    <h3 class="base_bold">
                        {{ $translate(post.title) }}
                    </h3>

                    <div v-if="post.user" class="transition-all ease-in-out">
                        <div class="flex flex-row gap-2 items-center">
                            <img v-if="post?.authorbox?.portrait" class="w-12 h-12 rounded-full object-cover bg-blue" :src="post.authorbox.portrait" :alt="post.user.name" />
                            <div class="text-sm">
                                <p style="line-height: 100%">{{ $t('author_male') }}:</p>
                                <strong>
                                    {{ post.user.name }}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="postImage" class="w-28 lg:w-32 h-24 lg:h-28 shrink-0">
                    <img :src="postImage" class="w-full h-full object-cover rounded-md shadow-md" />
                </div>
            </div>
        </div>
    </router-link>
</template>
